import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import InputTitle from 'components/Input/InputTitle'
import NumericInput from 'components/Input/NumericInput'
import Button from 'components/Button'
import SegmentedControl from 'components/SegmentedControl'
import { FlexDivRow } from 'components/layout/flex'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectCurrentMarketInfo, selectLiq2AmountRange, selectLiq2AmountRangeQueryStatus, selectPlaceLiquidityFinished } from 'state/exchange/selectors'
import { liq2AmountRange, placeLiquidity } from 'state/exchange/actions'
import { FetchStatus } from 'state/types'
import { selectCurrentMarketPrice } from 'state/prices/selectors'
import { formatNumber } from 'utils/prices'
import { notifyError } from 'components/ErrorNotifier'
import { point2Price, price2Point } from '@bitly/sdk/utils'

type Props = {
	mobile?: boolean
}

const Liquid: FC<Props> = memo(({ mobile }) => {
	const dispatch = useAppDispatch()
	const marketInfo = useAppSelector(selectCurrentMarketInfo)
	const curPrice = useAppSelector(selectCurrentMarketPrice)
	const liq2Range = useAppSelector(selectLiq2AmountRange)
	const liq2RangeStatus = useAppSelector(selectLiq2AmountRangeQueryStatus)
	const isPlaceLiquidityFinished = useAppSelector(selectPlaceLiquidityFinished)
	const { t } = useTranslation()

	const [minPrice, setMinPrice] = useState<string>('')
	const [maxPrice, setMaxPrice] = useState<string>('')
	const [minPriceError, setMinPriceError] = useState<string>('')
	const [maxPriceError, setMaxPriceError] = useState<string>('')
	const [liquidityScale, setLiquidityScale] = useState<string>('')
	// 0 = both, 1 = token X only, 2 = token Y only
	const [depositMode, setDepositMode] = useState<0 | 1 | 2>(0)

	const tokenXSymbol = marketInfo?.tokenX.symbol ?? 'TokenX'
	const tokenYSymbol = marketInfo?.tokenY.symbol ?? 'TokenY'
	const pointDelta = marketInfo?.detail?.pointDelta ?? 100
	const tokenXDecimals = marketInfo?.tokenX.decimals ?? 18
	const tokenYDecimals = marketInfo?.tokenY.decimals ?? 18

	const curPoint = useMemo(
		() => curPrice ? price2Point(curPrice, tokenXDecimals, tokenYDecimals, pointDelta) : 0,
		[curPrice, tokenXDecimals, tokenYDecimals, pointDelta]
	)

	const handleMinPriceChange = useCallback((_: any, v: string) => {
		setMinPrice(v)
		const nextPrice = point2Price(curPoint + pointDelta * 2, tokenXDecimals, tokenYDecimals)
		if (depositMode === 1 && curPrice && v !== '' && Number(v) < nextPrice) {
			setMinPriceError(t('futures.market.liquid.min-price-error', { nextPrice: formatNumber(nextPrice, { suggestDecimals: true }), token: tokenXSymbol }))
		} else {
			setMinPriceError('')
		}
	}, [depositMode, curPrice, curPoint, pointDelta, tokenXDecimals, tokenYDecimals, tokenXSymbol, t])

	const handleMaxPriceChange = useCallback((_: any, v: string) => {
		setMaxPrice(v)
		const nextPrice = point2Price(curPoint - pointDelta * 2, tokenXDecimals, tokenYDecimals)
		if (depositMode === 2 && curPrice && v !== '' && Number(v) > nextPrice) {
			setMaxPriceError(t('futures.market.liquid.max-price-error', { nextPrice: formatNumber(nextPrice, { suggestDecimals: true }), token: tokenYSymbol }))
		} else {
			setMaxPriceError('')
		}
	}, [depositMode, curPrice, curPoint, pointDelta, tokenXDecimals, tokenYDecimals, tokenYSymbol, t])

	const handleDepositModeChange = useCallback((index: number) => {
		const mode = index as 0 | 1 | 2
		setDepositMode(mode)
		setMinPriceError('')
		setMaxPriceError('')
		if (!curPrice) return
		if (mode === 1) {
			// token X only: min price must be >= curPrice
			const nextPrice = point2Price(curPoint + pointDelta * 2, tokenXDecimals, tokenYDecimals)
			if (!minPrice || Number(minPrice) < nextPrice) {
				setMinPrice(formatNumber(nextPrice, { suggestDecimals: true }))
			}
		} else if (mode === 2) {
			// token Y only: max price must be <= curPrice
			const nextPrice = point2Price(curPoint - pointDelta * 2, tokenXDecimals, tokenYDecimals)
			if (!maxPrice || Number(maxPrice) > nextPrice) {
				setMaxPrice(formatNumber(nextPrice, { suggestDecimals: true }))
			}
		}
	}, [curPrice, curPoint, minPrice, maxPrice, pointDelta, tokenXDecimals, tokenYDecimals])

	// Call liq2AmountRange when inputs change
	useEffect(() => {
		const low = Number(minPrice)
		const high = Number(maxPrice)
		const scale = Number(liquidityScale)
		if (curPrice && low > 0 && high > 0 && scale > 0 && scale <= 1) {
			dispatch(liq2AmountRange({ lowPrice: low, highPrice: high, liquidityScale: scale }))
		}
	}, [dispatch, minPrice, maxPrice, liquidityScale, curPrice])

	const handlePriceButtonClick = useCallback((
		type: 'min' | 'max',
		multiplier: number | 'infinity',
		basePrice: number = curPrice
	) => {
		if (multiplier === 'infinity') {
			if (type === 'max') {
				setMaxPrice('+∞')
			} else {
				setMinPrice('-∞')
			}
		} else {
			if (type === 'min') {
				let value = basePrice * (1 - multiplier)
				const nextPrice = point2Price(curPoint + pointDelta * 2, tokenXDecimals, tokenYDecimals)
				if (depositMode === 1 && curPrice && value < nextPrice) {
					value = nextPrice
				}
				setMinPrice(formatNumber(value, { suggestDecimals: true}))
			} else {
				let value = basePrice * (1 + multiplier)
				const nextPrice = point2Price(curPoint - pointDelta * 2, tokenXDecimals, tokenYDecimals)
				if (depositMode === 2 && curPrice && value > nextPrice) {
					value = nextPrice
				}
				setMaxPrice(formatNumber(value, { suggestDecimals: true}))
			}
		}
	}, [depositMode, curPrice, curPoint, pointDelta, tokenXDecimals, tokenYDecimals])

	const handleScaleButtonClick = (percent: number) => {
		setLiquidityScale((percent / 100).toFixed(2))
	}

	// Get token amounts from liq2Range result
	const tokenXAmount = liq2RangeStatus.status === FetchStatus.Success && liq2Range
		? formatNumber(liq2Range.amountX, { suggestDecimals: true})
		: '0.00'
	const tokenYAmount = liq2RangeStatus.status === FetchStatus.Success && liq2Range
		? formatNumber(liq2Range.amountY, { suggestDecimals: true})
		: '0.00'

	const handlePlaceLiquidity = useCallback(() => {
		const low = Number(minPrice)
		const high = Number(maxPrice)
		if (low > 0 && high > 0) {
			dispatch(placeLiquidity({ minPrice: low, maxPrice: high }))
		} else {
			notifyError('Please enter valid price range')
		}
	}, [dispatch, minPrice, maxPrice])

	const submitButtonDisabled = useMemo(() => {
		return minPrice === '' || maxPrice === '' || Number(minPrice) <= 0 || Number(maxPrice) <= 0
			|| ((tokenXAmount === '' || Number(tokenXAmount) === 0) && (tokenYAmount === '' || Number(tokenYAmount) === 0))
			|| !!minPriceError || !!maxPriceError
	}, [minPrice, maxPrice, tokenXAmount, tokenYAmount, minPriceError, maxPriceError])

	return (
		<LiquidContainer $mobile={mobile}>
			{/* Section 0: Deposit Mode */}
			<Section>
				<InputTitle>{t('futures.market.liquid.deposit-mode')}</InputTitle>
				<SegmentedControl
					values={[
						t('futures.market.liquid.deposit-both'),
						t('futures.market.liquid.deposit-x-only', { token: tokenXSymbol }),
						t('futures.market.liquid.deposit-y-only', { token: tokenYSymbol }),
					]}
					selectedIndex={depositMode}
					onChange={handleDepositModeChange}
				/>
			</Section>

			{/* Section 1: Liquidity Boundary */}
			<Section>
				<InputTitle>{t('futures.market.liquid.liquidity-boundary')}</InputTitle>
				<PriceInputsRow>
					<PriceInputWrapper>
						<InputTitleSmall>{t('futures.market.liquid.min-price')}</InputTitleSmall>
						<NumericInput
							value={minPrice}
							onChange={handleMinPriceChange}
							placeholder="0.00"
							invalid={!!minPriceError}
						/>
						{minPriceError && <ErrorMessage>{minPriceError}</ErrorMessage>}
					</PriceInputWrapper>
					<PriceInputWrapper>
						<InputTitleSmall>{t('futures.market.liquid.max-price')}</InputTitleSmall>
						<NumericInput
							value={maxPrice}
							onChange={handleMaxPriceChange}
							placeholder="0.00"
							invalid={!!maxPriceError}
						/>
						{maxPriceError && <ErrorMessage>{maxPriceError}</ErrorMessage>}
					</PriceInputWrapper>
				</PriceInputsRow>
				<ButtonsRow>
					{['±10%', '±20%', '±50%', '∞'].map((label, index) => (
						<Button
							key={label}
							size="xsmall"
							variant="flat"
							onClick={() => {
								const multiplier = index === 3 ? 'infinity' : Number(label.replace('±', '').replace('%', '')) / 100
								handlePriceButtonClick('min', multiplier)
								handlePriceButtonClick('max', multiplier)
							}}
						>
							{label}
						</Button>
					))}
				</ButtonsRow>
			</Section>

			{/* Section 2: Liquidity Scale */}
			<Section>
				<InputTitle>{t('futures.market.liquid.liquidity-scale')}</InputTitle>
				<ScaleInputWrapper>
					<NumericInput
						value={liquidityScale}
						onChange={(_, v) => {
							const num = Number(v)
							if (num >= 0.0 && num <= 1.0) {
								setLiquidityScale(v)
							}
						}}
						placeholder="0.0 - 1.0"
						max={1}
					/>
				</ScaleInputWrapper>
				<ButtonsRow>
					{['25%', '50%', '75%', '100%'].map((label) => (
						<Button
							key={label}
							size="xsmall"
							variant="flat"
							onClick={() => handleScaleButtonClick(Number(label.replace('%', '')))}
						>
							{label}
						</Button>
					))}
				</ButtonsRow>
			</Section>

			{/* Section 3: Token Needed */}
			<Section>
				<InputTitle>{t('futures.market.liquid.token-needed')}</InputTitle>
				<TokenRow>
					<TokenWrapper>
						<InputTitleSmall>
							${marketInfo?.tokenX.symbol ?? 'TokenX'}
						</InputTitleSmall>
						<TokenValue>{tokenXAmount}</TokenValue>
					</TokenWrapper>
					<TokenWrapper>
						<InputTitleSmall>
							${marketInfo?.tokenY.symbol ?? 'TokenY'}
						</InputTitleSmall>
						<TokenValue>{tokenYAmount}</TokenValue>
					</TokenWrapper>
				</TokenRow>
			</Section>

			{/* Section 4: Place Liquidity Button */}
			<Section>
				<PlaceLiquidityButton
					size="large"
					fullWidth
					noOutline
					onClick={handlePlaceLiquidity}
					loading={!isPlaceLiquidityFinished}
					disabled={submitButtonDisabled}
				>
					{t('futures.market.liquid.place-liquidity')}
				</PlaceLiquidityButton>
			</Section>
		</LiquidContainer>
	)
})

const LiquidContainer = styled.div<{ $mobile?: boolean }>`
	padding: 8px;
	height: 100%;
	display: flex;
	flex-direction: column;
	gap: 36px;
`

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`

const PriceInputsRow = styled(FlexDivRow)`
	gap: 12px;
`

const PriceInputWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	flex: 1;
`

const InputTitleSmall = styled(InputTitle)`
	font-size: 12px;
`

const ScaleInputWrapper = styled.div`
	width: 100%;
`

const ButtonsRow = styled.div`
	display: grid;
	grid-template-columns: repeat(5, 1fr);
	gap: 8px;

	${(props) =>
		props.children &&
		React.Children.count(props.children) === 4 &&
		css`
			grid-template-columns: repeat(4, 1fr);
		`}
`

const TokenRow = styled(FlexDivRow)`
	gap: 12px;
`

const TokenWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	flex: 1;
`

const TokenValue = styled.span`
	font-family: ${(props) => props.theme.fonts.mono};
	font-size: 18px;
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	padding: 8px 10px;
	background: ${(props) => props.theme.colors.selectedTheme.input.background};
	border: ${(props) => props.theme.colors.selectedTheme.border};
	border-radius: 8px;
	min-height: 38px;
	display: flex;
	align-items: center;
	box-sizing: border-box;
`

const ErrorMessage = styled.div`
	color: ${(props) => props.theme.colors.selectedTheme.red};
	font-size: 12px;
	margin-top: 4px;
`

const PlaceLiquidityButton = styled(Button)`
	background: #0080ff;
	color: #ffffff;

	&:hover:not(:disabled) {
		background: #0073e6;
	}
`

export default Liquid
